import { Button } from "@/marketing/components/ui/button";
import { Input } from "@/marketing/components/ui/input";

import { ImageUploadField } from "./ImageUploadField";
import type {
  BriefStructuredFormState,
  DeliverableRow,
  FaqRow,
  PastResultRow,
  TimelineRow,
} from "./briefStructuredForm";

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <label className="tw-mb-1 tw-block tw-text-xs tw-font-semibold tw-text-foreground/80">
      {children}
    </label>
  );
}

// Tinted card with an emoji header, mirroring the mockup's grouped brief sections.
function BriefCard({
  icon,
  title,
  children,
}: {
  icon: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="tw-rounded-lg tw-border tw-border-border tw-bg-muted/30 tw-p-4">
      <div className="tw-mb-3 tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-bold tw-text-foreground/80">
        <span aria-hidden>{icon}</span> {title}
      </div>
      {children}
    </div>
  );
}

interface BriefStructuredSectionProps {
  value: BriefStructuredFormState;
  onChange: (next: BriefStructuredFormState) => void;
}

export function BriefStructuredSection({ value, onChange }: BriefStructuredSectionProps) {
  const patch = (changes: Partial<BriefStructuredFormState>) =>
    onChange({ ...value, ...changes });

  const updateDeliverable = (idx: number, changes: Partial<DeliverableRow>) =>
    patch({
      deliverables: value.deliverables.map((row, i) =>
        i === idx ? { ...row, ...changes } : row,
      ),
    });
  const updateTimeline = (idx: number, changes: Partial<TimelineRow>) =>
    patch({
      timeline: value.timeline.map((row, i) =>
        i === idx ? { ...row, ...changes } : row,
      ),
    });
  const updateFaq = (idx: number, changes: Partial<FaqRow>) =>
    patch({ faq: value.faq.map((row, i) => (i === idx ? { ...row, ...changes } : row)) });
  const updatePastResult = (idx: number, changes: Partial<PastResultRow>) =>
    patch({
      pastResults: value.pastResults.map((row, i) =>
        i === idx ? { ...row, ...changes } : row,
      ),
    });

  return (
    <div>
      <div className="tw-text-xs tw-font-semibold tw-text-foreground/80">
        Structured brief (optional, recommended for portal Detail screen)
      </div>
      <div className="tw-mt-3 tw-grid tw-gap-4">
        <div className="tw-grid tw-grid-cols-2 tw-gap-3">
          <div>
            <FieldLabel>Required hashtags (comma-separated)</FieldLabel>
            <Input
              value={value.requiredHashtagsRaw}
              onChange={(e) => patch({ requiredHashtagsRaw: e.target.value })}
              placeholder="#anua, #glow, #skinseoul"
            />
          </div>
          <div>
            <FieldLabel>Prohibited words / brands (comma-separated)</FieldLabel>
            <Input
              value={value.prohibitedRaw}
              onChange={(e) => patch({ prohibitedRaw: e.target.value })}
              placeholder="competitor-brand, banned-claim"
            />
          </div>
        </div>
        <div className="tw-grid tw-grid-cols-2 tw-gap-3">
          <div>
            <FieldLabel>Min followers</FieldLabel>
            <Input
              type="number"
              min={0}
              value={value.minFollowers}
              onChange={(e) => patch({ minFollowers: e.target.value })}
              placeholder="e.g. 1000"
            />
          </div>
          <div>
            <FieldLabel>Min tier (0=Starter … 4=Ambassador)</FieldLabel>
            <Input
              type="number"
              min={0}
              max={4}
              value={value.minTier}
              onChange={(e) => patch({ minTier: e.target.value })}
              placeholder="e.g. 1"
            />
          </div>
        </div>

        <div>
          <FieldLabel>What brands look for (one criterion per line)</FieldLabel>
          <textarea
            value={value.brandLookForRaw}
            onChange={(e) => patch({ brandLookForRaw: e.target.value })}
            rows={3}
            placeholder={"Skin-tone diversity & honest reviews\nEngagement rate ≥ 3.5%\nCalm, editorial tone"}
            className="tw-min-h-[72px] tw-w-full tw-resize-y tw-rounded-md tw-border tw-border-input tw-bg-background tw-px-3 tw-py-2 tw-text-sm"
          />
        </div>

        <BriefCard icon="📄" title="Usage rights">
          <div className="tw-grid tw-grid-cols-3 tw-gap-3">
            <div>
              <FieldLabel>Freedom</FieldLabel>
              <Input
                value={value.usageFreedom}
                onChange={(e) => patch({ usageFreedom: e.target.value })}
                placeholder="Balanced"
              />
            </div>
            <div>
              <FieldLabel>Period</FieldLabel>
              <Input
                value={value.usagePeriod}
                onChange={(e) => patch({ usagePeriod: e.target.value })}
                placeholder="12 months"
              />
            </div>
            <div>
              <FieldLabel>Scope</FieldLabel>
              <Input
                value={value.usageScope}
                onChange={(e) => patch({ usageScope: e.target.value })}
                placeholder="Brand social + paid Spark Ads"
              />
            </div>
          </div>
        </BriefCard>

        <BriefCard icon="📦" title="Deliverables">
          <div className="tw-flex tw-flex-col tw-gap-2">
            {value.deliverables.map((row, idx) => (
              <div
                key={idx}
                className="tw-grid tw-grid-cols-[1fr_80px_2fr_auto] tw-items-center tw-gap-2"
              >
                <Input
                  value={row.type}
                  onChange={(e) => updateDeliverable(idx, { type: e.target.value })}
                  placeholder="reel"
                />
                <Input
                  type="number"
                  min={0}
                  value={row.count}
                  onChange={(e) => updateDeliverable(idx, { count: e.target.value })}
                  placeholder="1"
                />
                <Input
                  value={row.description}
                  onChange={(e) =>
                    updateDeliverable(idx, { description: e.target.value })
                  }
                  placeholder="60s vertical, brand in first 3s"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    patch({
                      deliverables: value.deliverables.filter((_, i) => i !== idx),
                    })
                  }
                >
                  ✕
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                patch({
                  deliverables: [
                    ...value.deliverables,
                    { type: "", count: "", description: "" },
                  ],
                })
              }
            >
              + Add deliverable
            </Button>
          </div>
        </BriefCard>

        <BriefCard icon="🗓️" title="Timeline">
          <div className="tw-flex tw-flex-col tw-gap-2">
            {value.timeline.map((row, idx) => (
              <div
                key={idx}
                className="tw-grid tw-grid-cols-[2fr_150px_auto] tw-items-center tw-gap-2"
              >
                <Input
                  value={row.label}
                  onChange={(e) => updateTimeline(idx, { label: e.target.value })}
                  placeholder="Brief signed"
                />
                <Input
                  type="date"
                  value={row.date}
                  onChange={(e) => updateTimeline(idx, { date: e.target.value })}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    patch({ timeline: value.timeline.filter((_, i) => i !== idx) })
                  }
                >
                  ✕
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                patch({ timeline: [...value.timeline, { label: "", date: "" }] })
              }
            >
              + Add timeline step
            </Button>
          </div>
        </BriefCard>

        <BriefCard icon="❓" title="FAQ">
          <div className="tw-flex tw-flex-col tw-gap-2">
            {value.faq.map((row, idx) => (
              <div
                key={idx}
                className="tw-grid tw-grid-cols-[1fr_1fr_auto] tw-items-start tw-gap-2"
              >
                <textarea
                  value={row.question}
                  onChange={(e) => updateFaq(idx, { question: e.target.value })}
                  rows={2}
                  placeholder="Q: Can I use other brands in the same video?"
                  className="tw-min-h-[52px] tw-w-full tw-resize-y tw-rounded-md tw-border tw-border-input tw-bg-background tw-px-3 tw-py-2 tw-text-sm"
                />
                <textarea
                  value={row.answer}
                  onChange={(e) => updateFaq(idx, { answer: e.target.value })}
                  rows={2}
                  placeholder="A: ..."
                  className="tw-min-h-[52px] tw-w-full tw-resize-y tw-rounded-md tw-border tw-border-input tw-bg-background tw-px-3 tw-py-2 tw-text-sm"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    patch({ faq: value.faq.filter((_, i) => i !== idx) })
                  }
                >
                  ✕
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => patch({ faq: [...value.faq, { question: "", answer: "" }] })}
            >
              + Add FAQ
            </Button>
          </div>
        </BriefCard>

        <BriefCard icon="🏆" title="Past results">
          <div className="tw-flex tw-flex-col tw-gap-2">
            {value.pastResults.map((row, idx) => (
              <div
                key={idx}
                className="tw-grid tw-grid-cols-[2fr_1fr_auto] tw-items-center tw-gap-2"
              >
                <Input
                  value={row.label}
                  onChange={(e) => updatePastResult(idx, { label: e.target.value })}
                  placeholder="Average views"
                />
                <Input
                  value={row.metric}
                  onChange={(e) => updatePastResult(idx, { metric: e.target.value })}
                  placeholder="50k"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    patch({
                      pastResults: value.pastResults.filter((_, i) => i !== idx),
                    })
                  }
                >
                  ✕
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                patch({
                  pastResults: [...value.pastResults, { label: "", metric: "" }],
                })
              }
            >
              + Add past result
            </Button>
          </div>
        </BriefCard>

        <ImageUploadField
          label="Thumbnail"
          value={value.thumbnailUrl}
          onChange={(thumbnailUrl) => patch({ thumbnailUrl })}
          enableCrop
        />

        <div>
          <FieldLabel>Campaign images (one URL per line)</FieldLabel>
          <textarea
            value={value.imagesRaw}
            onChange={(e) => patch({ imagesRaw: e.target.value })}
            rows={3}
            placeholder="https://cdn.example.com/campaign-1.jpg"
            className="tw-min-h-[72px] tw-w-full tw-resize-y tw-rounded-md tw-border tw-border-input tw-bg-background tw-px-3 tw-py-2 tw-text-sm"
          />
        </div>
      </div>
    </div>
  );
}
